"use client";
import React, { useEffect } from "react";
import {
  GetUserAccountsDocument,
  IamUser,
  NewUserDocument,
  RequestResetPasswordDocument,
  SetUserStatusDocument,
  UpdateUserByIdDocument,
} from "@quales/data-access/gql-server";
import { useTranslations } from "next-intl";
import { useWindowDimensions } from "@quales/utils/hooks";
import { useTheme } from "@quales/utils/theme";
import { Add } from "@mui/icons-material";
import { SubHeader } from "../ui/layout";
import { UserForm } from "../forms";
import { ModalButtonBar } from "../ui/button";
import Edit from "@mui/icons-material/Edit";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { TourContext } from "@quales/utils/context";
import { enqueueSnackbar } from "notistack";

import { UserTour } from "./TourData";
import { useMutation, useQuery } from "@apollo/client";
import {
  WcButton,
  WcChip,
  WcDialog,
  WcModal,
  WcStack,
} from "@bpipartners/webui";
import {
  WcGridActionsCellItem,
  WcGridColDef,
  WcGridRenderCellParams,
  WcGridRowParams,
  WcDataGrid,
} from "@bpipartners/datagrid";
import { Header } from "../ui/header";

export const UserManagementComposite = () => {
  const windowSize = useWindowDimensions();
  const t = useTranslations();
  const theme = useTheme();
  const createUserRef = React.useRef();
  const editUserRef = React.useRef();

  const [userList, setUserList] = React.useState<IamUser[]>([]);
  // const { tour } = React.useContext(TourContext);
  const [isInActive, setIsInActive] = React.useState(false);
  const { loading, data } = useQuery(GetUserAccountsDocument, {
    variables: { active: !isInActive },
    fetchPolicy: "network-only",
  });

  const [runTour, setRunTour] = React.useState(false);

  // useEffect(() => {
  //   if (tour && tour.tourID === "2") {
  //     setRunTour(true);
  //   }
  // }, [tour]);

  const handleTour = (tourID: string) => {
    switch (tourID) {
      case "2":
        return <UserTour onAddUserModalClicked={onAddModalPressed} />;

      default:
        return <div></div>;
    }
  };

  useEffect(() => {
    if (!loading && data && data.UserAccounts && data.UserAccounts.length > 0) {
      setUserList(data.UserAccounts);
    } else {
      setUserList([]);
    }
  }, [data, loading]);

  const [modalOpen, setModalOpen] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState<IamUser>(null);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

  const onClosedPressed = () => {
    setModalOpen(false);
  };

  const editUser = React.useCallback(
    (item: WcGridRowParams) => () => {
      setTimeout(() => {
        setModalOpen(true);
        setCurrentUser(item.row);
      });
    },
    []
  );

  const setUserStatus = React.useCallback(
    (item: WcGridRowParams) => () => {
      setTimeout(() => {
        setShowConfirmDialog(true);
        setCurrentUser(item.row);
      });
    },
    []
  );

  const handleOnRestetCompleted = function () {
    setTimeout(() => {
      enqueueSnackbar(t("feedback.userResetLinkSent"), {
        variant: "success",
      });

      setModalOpen(false);
      setShowConfirmDialog(false);
      setCurrentUser(null);
    }, 1000);
  };

  const [sendResetLink] = useMutation(RequestResetPasswordDocument, {
    onCompleted: handleOnRestetCompleted,
  });

  const sendRequestPasswordReset = React.useCallback(
    (item: WcGridRowParams) => () => {
      sendResetLink({
        variables: {
          //@ts-ignore
          id: item.id,
        },
      });
    },
    [sendResetLink]
  );

  const UserColumnData: WcGridColDef[] = [
    {
      field: "emailAddress",
      headerName: t("labels.emailAddress"),
      minWidth: 210,
      flex: 0.5,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: "userName",
      headerName: t("labels.userName"),
      minWidth: 230,
      flex: 0.3,
      sortable: true,
      disableColumnMenu: true,
      valueGetter: (params, row) =>
        row.firstName + " " + row.infix + " " + row.lastName,
    },
    {
      field: "roles",
      headerName: t("labels.roles"),
      minWidth: 350,
      flex: 0.5,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params: WcGridRenderCellParams) => (
        <WcStack
          direction="row"
          spacing={1}
          padding={1}
          alignItems={"center"}
          height={"100%"}
        >
          {params.row.roles &&
            params.row.roles.length > 0 &&
            params.row.roles.map((role, index) => (
              <WcChip
                key={index}
                sx={{
                  alignSelf: "center",
                  height: "16px",
                  backgroundColor: theme.appColors.primaryContainer,
                  color: theme.appColors.onPrimaryContainer,
                }}
                label={role.roleName}
              />
            ))}
        </WcStack>
      ),
    },
    {
      field: "branchName",
      headerName: t("labels.branchName"),
      width: 250,
      flex: 0.4,
      sortable: true,
      disableColumnMenu: true,
    },

    {
      field: "active",
      headerName: t("labels.active"),
      minWidth: 60,
      flex: 0.1,
      sortable: false,
      disableColumnMenu: true,
      type: "boolean",
    },
    {
      field: "actions",
      type: "actions",
      minWidth: 100,
      flex: 0.2,
      headerName: t("labels.actions"),
      getActions: (params: WcGridRowParams) => [
        <WcGridActionsCellItem
          key={1}
          icon={<Edit color="primary" titleAccess={t("labels.editUser")} />}
          label={t("labels.editUser")}
          onClick={editUser(params)}
        />,
        <WcGridActionsCellItem
          key={2}
          icon={
            <PersonOffIcon
              color="primary"
              titleAccess={t("labels.setUserStatus")}
            />
          }
          label={t("labels.setUserStatus")}
          onClick={setUserStatus(params)}
        />,
        <WcGridActionsCellItem
          key={2}
          icon={
            <LockOpenIcon
              color="primary"
              titleAccess={t("labels.sendResetPasswordLink")}
            />
          }
          label={t("labels.sendResetPasswordLink")}
          onClick={sendRequestPasswordReset(params)}
        />,
      ],
    },
  ];

  const [addUserModal, setAddUserModal] = React.useState<boolean>(false);

  const onAddModalPressed = () => {
    setAddUserModal(!addUserModal);
  };

  const handleSubmit = () => {
    if (createUserRef && createUserRef.current) {
      //@ts-ignore
      createUserRef.current.onSubmit();
    }
  };

  const handleEditSubmit = () => {
    if (editUserRef && editUserRef.current) {
      //@ts-ignore
      editUserRef.current.onSubmit();
    }
  };

  const handleOnMutationCompleted = function () {
    setTimeout(() => {
      enqueueSnackbar(t("feedback.createSuccess"), {
        variant: "success",
      });

      setAddUserModal(false);
    }, 1000);
  };

  const handleOnUpdateCompleted = function () {
    setTimeout(() => {
      enqueueSnackbar(t("feedback.updateSuccess"), {
        variant: "success",
      });
      setModalOpen(false);
      setShowConfirmDialog(false);
      setCurrentUser(null);
    }, 1000);
  };

  const [newUser, { error: newUserError }] = useMutation(NewUserDocument, {
    onCompleted: handleOnMutationCompleted,
    refetchQueries: [
      {
        query: GetUserAccountsDocument,
        variables: {
          active: !isInActive,
        },
      },
    ],
  });

  const [updateUser] = useMutation(UpdateUserByIdDocument, {
    onCompleted: handleOnUpdateCompleted,
    refetchQueries: [
      {
        query: GetUserAccountsDocument,
        variables: {
          active: !isInActive,
        },
      },
    ],
  });
  const [updateUserStatus] = useMutation(SetUserStatusDocument, {
    onCompleted: handleOnUpdateCompleted,
    refetchQueries: [
      {
        query: GetUserAccountsDocument,
        variables: {
          active: !isInActive,
        },
      },
    ],
  });

  const handleIsActiveChanged = () => {
    setIsInActive(!isInActive);
  };

  const handleNewUser = (item: IamUser) => {
    newUser({
      variables: {
        input: {
          emailAddress: item.emailAddress,
          firstName: item.firstName,
          infix: item.infix,
          lastName: item.lastName,

          branchID: item.branchID ? item.branchID : null,
          //@ts-ignore
          roles: item.roles.map((role: GQLIamRole) => role.id),
        },
      },
    }).catch((error) => {
      const errorMsg =
        error &&
        error.graphQLErrors &&
        error.graphQLErrors.length > 0 &&
        error.graphQLErrors[0].extensions.code;
      enqueueSnackbar(t("feedback." + errorMsg), {
        variant: "error",
      });
    });
  };

  const onCloseItem = () => {
    setShowConfirmDialog(false);
  };

  const onRemoveItem = () => {
    updateUserStatus({
      variables: {
        active: !currentUser.active,
        id: currentUser.userID,
      },
    });
    setShowConfirmDialog(false);
  };

  const handleUserUpdate = (item: IamUser) => {
    updateUser({
      variables: {
        input: {
          firstName: item.firstName,
          infix: item.infix,
          lastName: item.lastName,
          userID: item.userID,
          branchID: item.branchID || null,
          //@ts-ignore
          roles: item.roles.map((role: GQLIamRole) => role.id),
        },
      },
    });
  };

  return (
    <WcStack id="UserManagementComposite">
      <Header
        pageTitle={t("common.userOverview")}
        pageTitleTypographyProps={{
          variant: "h5",
          color: theme.appColors.primary,
        }}
      >
        <WcStack direction="row-reverse" spacing={2} marginRight={4}>
          <WcButton
            id={"userCreate"}
            variant="outlined"
            color="secondary"
            onClick={onAddModalPressed}
            startIcon={<Add />}
          >
            {t("labels.newUser")}
          </WcButton>
        </WcStack>
      </Header>
      <WcStack
        spacing={2}
        width={"100%"}
        height={windowSize.height - theme.dimensions.totalTopbarHeight}
      >
        {!loading && userList && (
          <WcDataGrid
            columns={UserColumnData}
            rowHeight={60}
            getCustomID={(row) => row.userID}
            rows={userList}
          />
        )}
      </WcStack>
      {/* {runTour && handleTour(tour.tourID)} */}

      {addUserModal && (
        <WcModal
          modalTitle={t("common.addUser")}
          open={addUserModal}
          closeHandler={onAddModalPressed}
        >
          <WcStack id="userModal" width={"100%"}>
            <WcStack direction={"row"} justifyContent="flex-end">
              <ModalButtonBar
                disabled={false}
                onEdit={null}
                isCreate={true}
                onSave={() => handleSubmit()}
              />
            </WcStack>
            <WcStack
              id="userBody"
              alignItems={"center"}
              width="100%"
              direction={"column"}
            >
              <UserForm onUserDetails={handleNewUser} ref={createUserRef} />
            </WcStack>
          </WcStack>
        </WcModal>
      )}

      {modalOpen && currentUser && (
        <WcModal
          modalTitle={t("common.editUser")}
          open={modalOpen}
          closeHandler={onClosedPressed}
        >
          <WcStack width={"100%"}>
            <WcStack direction={"row"} justifyContent="flex-end">
              <ModalButtonBar
                disabled={false}
                onEdit={null}
                isCreate={true}
                onSave={() => handleEditSubmit()}
              />
            </WcStack>
            <WcStack alignItems={"center"} width="100%" direction={"column"}>
              <UserForm
                onUserDetails={handleUserUpdate}
                ref={editUserRef}
                data={currentUser}
              />
            </WcStack>
          </WcStack>
        </WcModal>
      )}

      {showConfirmDialog && (
        <WcDialog
          open={showConfirmDialog}
          leftButtonHeading={t("common.cancel")}
          rightButtonHeading={t("common.confirm")}
          handleCancel={onCloseItem}
          handleClosePressed={onRemoveItem}
          heading={
            t("feedback.userStatusConfirm") +
            ` ${
              currentUser.firstName +
              " " +
              currentUser.infix +
              " " +
              currentUser.lastName
            } ?`
          }
        />
      )}
    </WcStack>
  );
};

export default UserManagementComposite;
